import React, { useState, useEffect } from "react";
import { NodeProps, useEdges, useNodes, useReactFlow } from "reactflow";
import styled from 'styled-components';
import axios from 'axios';
import { operationType } from "../../../../../services/types";
import { SettingsBlock, settingsDataType } from "../../../../settingsBlock";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";
import { ExpandedBlock } from "../../../expanded";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
    faMicrophone, faStop, faTrash, faCircle, faLink, faSpinner, faUpload, faFileAudio
} from "@fortawesome/free-solid-svg-icons";
import { useAudioRecorder } from "./useAudioRecorder";
import { WaveformVisualizer } from "./WaveformVisualizer";
import { store } from "../../../../../redux/store";
import { webURL } from "../../../../../services/API";
import { addUpdateNodeToQueue } from "../../../../../services/method";

type SpeechToTextProps = NodeProps<operationType> & {
    setChildMinimize?: (fn: (id: string) => void) => void;
    setChildSettings?: (fn: (id: string) => void) => void;
};

export function SpeechToText({ id, data, setChildMinimize, setChildSettings }: SpeechToTextProps) {
    const { setNodes } = useReactFlow();
    const nodes = useNodes();
    const edges = useEdges();
    const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
    const [isExpanded, setIsExpanded] = React.useState(false);
    
    // Recorder state
    const recorder = useAudioRecorder();
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);

    // active tab
    const [activeTab, setActiveTab] = useState<'record' | 'connected'>(data.value?.activeTab || 'record');

    // --
    React.useEffect(() => {
        if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev) });
        if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })) });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setChildMinimize, setChildSettings]);

    // Initialize default values
    React.useEffect(() => {
        if (!data.value) {
            setNodes((nds) => nds.map((node) => {
                if (node.id === id) return { ...node, data: { ...node.data, value: { activeTab: 'record' } } };
                else return node;
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateNodeValueData = (newData: Partial<any>) => {
        setNodes((nds) => nds.map((node) => {
            if (node.id === id) {
                return { ...node, data: { ...node.data, value: { ...node.data.value, ...newData } } };
            }
            return node;
        }));
    };

    const handleTabChange = (tab: 'record' | 'connected') => {
        setActiveTab(tab);
        updateNodeValueData({ activeTab: tab });
    }

    const handleToggleStreamMode = () => {
        updateNodeValueData({ streamMode: !data.value?.streamMode });
    };

    // Auto-upload when recording stops and we have a blob
    useEffect(() => {
        const uploadRecordedAudio = async () => {
             if (recorder.audioBlob && !recorder.isRecording && !data.value?.audioUrl && !uploading) {
                 setUploading(true);
                 setUploadError(null);
                 try {
                     const request = new FormData();
                     request.append('file', recorder.audioBlob, `recording_${Date.now()}.${recorder.audioBlob.type.includes('webm') ? 'webm' : 'ogg'}`);
                     
                     const response = (await axios.post(`${webURL}/general/uploader`, request, { 
                         headers: { 
                             'Content-Type': 'multipart/form-data', 
                             'x-auth-token': store.getState().user.data?.token 
                         }
                     })).data;
                     
                     updateNodeValueData({ 
                         audioUrl: response.url,
                         audioFileName: 'Recorded Audio'
                     });
                 } catch (err: any) {
                     setUploadError(`Failed to upload recording: ${err.message}`);
                 } finally {
                     setUploading(false);
                 }
             }
        };
        uploadRecordedAudio();
    }, [recorder.audioBlob, recorder.isRecording]);


    const handleDiscard = () => {
        recorder.discardRecording();
        updateNodeValueData({ audioUrl: undefined, audioFileName: undefined });
        setUploadError(null);
    };

    // Check for connected files/urls
    // @ts-ignore
    const targetFile = edges.filter(edge => edge.target === id && nodes.some(node => node.id === edge.source && node.data?.action === 'fileUploader'));
    const relationsFile = nodes.filter(node => targetFile.some(edge => edge.source === node.id));
    // @ts-ignore
    const targetExternal = edges.filter(edge => edge.target === id && nodes.some(node => node.id === edge.source && node.data?.outputTypeSub === 'fileURL'));
    const relationsExternal = nodes.filter(node => targetExternal.some(edge => edge.source === node.id));

    // ---
    const isWarning = (!relationsFile.length && !relationsExternal.length && activeTab === 'connected') || 
                      (activeTab === 'record' && !data.value?.audioUrl && !recorder.isRecording);

    const formatTime = (seconds: number) => {
        const m = Math.floor(seconds / 60).toString().padStart(2, '0');
        const s = (seconds % 60).toString().padStart(2, '0');
        return `${m}:${s}`;
    };

    return (<>
        <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
        <Main>
            {isWarning ? <>
                <div id="warning"></div>
                <span id="warningIcon"><IoWarningOutline size={21} /></span>
            </> : data.output ? <>
                <div id="ran"></div>
                <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
            </> : null}
            {isExpanded ? <ExpandedBlock
                data={{ id, ...data }}
                handleClose={() => setIsExpanded(false)}
                isWarning={isWarning}
                style={{ minWidth: '380px' }}
            ><Styled>
                    <div className="info">
                        <span><FontAwesomeIcon icon={ faMicrophone } /> Audio to Text Transcription</span>
                    </div>

                    <div className="tabs">
                        <div 
                            className={`tab ${activeTab === 'record' ? 'active' : ''}`}
                            onClick={() => handleTabChange('record')}
                        >
                            <FontAwesomeIcon icon={faMicrophone} /> Record
                        </div>
                        <div 
                            className={`tab ${activeTab === 'connected' ? 'active' : ''}`}
                            onClick={() => handleTabChange('connected')}
                        >
                            <FontAwesomeIcon icon={faLink} /> Connected Nodes
                        </div>
                    </div>

                    <div className="streamToggle">
                        <label>
                            <input
                                type="checkbox"
                                checked={data.value?.streamMode || false}
                                onChange={handleToggleStreamMode}
                            />
                            <span>Streaming Mode</span>
                        </label>
                        <span className="hint">Show transcription text as it is generated</span>
                    </div>

                    {activeTab === 'record' && (
                        <div className="record-container">
                            {/* Has recorded audio */}
                            {data.value?.audioUrl ? (
                                <div className="result-container">
                                    <div className="audio-header">
                                        <div className="audio-title">
                                            <FontAwesomeIcon icon={faFileAudio} className="text-blue" />
                                            <span>{data.value?.audioFileName || 'Recorded Audio'}</span>
                                        </div>
                                        <div className="discard-btn" onClick={handleDiscard}>
                                            <FontAwesomeIcon icon={faTrash} />
                                        </div>
                                    </div>
                                    <audio controls src={data.value.audioUrl} className="audio-player">
                                        Your browser does not support the audio element.
                                    </audio>
                                </div>
                            ) : (
                                /* Recording state */
                                <div className="recording-panel">
                                    {recorder.error && (
                                        <div className="error-msg">
                                            <IoWarningOutline /> {recorder.error}
                                        </div>
                                    )}
                                    {uploadError && (
                                        <div className="error-msg">
                                            <IoWarningOutline /> {uploadError}
                                        </div>
                                    )}
                                    
                                    <div className="visualizer-wrapper">
                                        <WaveformVisualizer 
                                            analyserNode={recorder.analyserNode} 
                                            isRecording={recorder.isRecording} 
                                        />
                                        <div className="duration">
                                            <FontAwesomeIcon icon={faCircle} className={recorder.isRecording ? 'recording-dot' : 'idle-dot'} />
                                            <span>{formatTime(recorder.duration)}</span>
                                        </div>
                                    </div>

                                    <div className="controls">
                                        {!recorder.isRecording && !uploading && (
                                            <button className="btn-start" onClick={recorder.startRecording}>
                                                <FontAwesomeIcon icon={faMicrophone} /> Start Recording
                                            </button>
                                        )}
                                        {recorder.isRecording && (
                                            <button className="btn-stop" onClick={recorder.stopRecording}>
                                                <FontAwesomeIcon icon={faStop} /> Stop Recording
                                            </button>
                                        )}
                                        {uploading && (
                                            <button className="btn-uploading" disabled>
                                                <FontAwesomeIcon icon={faSpinner} spin /> Uploading...
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'connected' && (
                        <div className="inputs nodrag nowheel">
                            <div className="title">Audio Sources</div>
                            <div className="item">
                                <div>
                                    <span className={relationsFile.length ? 'green' : 'red'}></span>
                                    <span>File Uploader</span>
                                </div>
                                <ul>{ // @ts-ignore
                                    relationsFile.length ? relationsFile.map(node => (<li key={node.id}>{node.data?.index}</li>)) : <li>N/A</li>
                                }</ul>
                            </div>
                            <div className="item">
                                <div>
                                    <span className={relationsExternal.length ? 'green' : 'red'}></span>
                                    <span>External Audio</span>
                                </div>
                                <ul>{ // @ts-ignore
                                    relationsExternal.length ? relationsExternal.map(node => (<li key={node.id}>{node.data?.index}</li>)) : <li>N/A</li>
                                }</ul>
                            </div>
                        </div>
                    )}
                </Styled></ExpandedBlock> : null}
        </Main>
    </>);
}

// ---
const Main = styled.div`
   min-width: 150px;
   padding: 0;
`;

const Styled = styled.div`
   .info {
      font-size: 12px;
      color: #888;
      margin-bottom: 8px;
   }

   .title {
      font-size: 13px;
      font-weight: 600;
      margin-top: 12px;
      margin-bottom: 10px;
   }

   .tabs {
       display: flex;
       gap: 8px;
       margin-bottom: 12px;
       background: #f1f5f9;
       padding: 4px;
       border-radius: 8px;

       .tab {
           flex: 1;
           text-align: center;
           padding: 6px 10px;
           font-size: 12px;
           font-weight: 500;
           color: #64748b;
           cursor: pointer;
           border-radius: 6px;
           transition: all 0.2s;
           display: flex;
           align-items: center;
           justify-content: center;
           gap: 6px;

           &.active {
               background: #fff;
               color: #0f172a;
               box-shadow: 0 1px 3px rgba(0,0,0,0.1);
           }
           &:hover:not(.active) {
               background: #e2e8f0;
           }
       }
   }

   .record-container {
        display: flex;
        flex-direction: column;
        gap: 12px;
   }

   .recording-panel {
       background: #fff;
       border: 1px solid #e2e8f0;
       border-radius: 8px;
       padding: 12px;
       display: flex;
       flex-direction: column;
       gap: 12px;

       .visualizer-wrapper {
           position: relative;
       }

       .duration {
           position: absolute;
           top: 8px;
           right: 8px;
           background: rgba(255,255,255,0.8);
           padding: 2px 6px;
           border-radius: 4px;
           font-size: 11px;
           font-weight: 600;
           display: flex;
           align-items: center;
           gap: 6px;
           color: #333;

           .recording-dot {
               color: #ef4444;
               animation: blink 1s infinite alternate;
           }
           .idle-dot {
               color: #94a3b8;
           }
       }

       .controls {
           display: flex;
           justify-content: center;
           
           button {
               width: 100%;
               padding: 8px 16px;
               border: none;
               border-radius: 6px;
               font-size: 13px;
               font-weight: 600;
               cursor: pointer;
               display: flex;
               align-items: center;
               justify-content: center;
               gap: 8px;
               transition: all 0.2s;

               &.btn-start {
                   background: #ef4444;
                   color: white;
                   &:hover { background: #dc2626; }
               }
               &.btn-stop {
                   background: #1e293b;
                   color: white;
                   &:hover { background: #0f172a; }
               }
               &.btn-uploading {
                   background: #94a3b8;
                   color: white;
                   cursor: not-allowed;
               }
           }
       }
   }

   .result-container {
        background: #f8fafc;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        padding: 10px;

        .audio-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 8px;

            .audio-title {
                display: flex;
                align-items: center;
                gap: 6px;
                font-size: 13px;
                font-weight: 500;
                color: #334155;
                .text-blue { color: #3b82f6; }
            }

            .discard-btn {
                color: #94a3b8;
                cursor: pointer;
                padding: 4px;
                border-radius: 4px;
                transition: all 0.2s;
                &:hover {
                    color: #ef4444;
                    background: #fee2e2;
                }
            }
        }

        .audio-player {
            width: 100%;
            height: 36px;
            outline: none;
        }
   }

   .error-msg {
       color: #ef4444;
       font-size: 12px;
       background: #fee2e2;
       padding: 8px;
       border-radius: 6px;
       display: flex;
       align-items: center;
       gap: 6px;
   }

   @keyframes blink {
       0% { opacity: 1; }
       100% { opacity: 0.3; }
   }

   .streamToggle {
      margin-top: 12px;
      margin-bottom: 8px;
      label {
         display: flex;
         align-items: center;
         gap: 8px;
         font-size: 13px;
         font-weight: 500;
         cursor: pointer;
         input {
            cursor: pointer;
            width: 16px;
            height: 16px;
         }
      }
      .hint {
         display: block;
         font-size: 11px;
         color: #888;
         margin-top: 4px;
         margin-left: 24px;
      }
   }

   .inputs {
      max-height: 120px;
      overflow-y: auto;
      
      .item {
         display: flex;
         align-items: center;
         justify-content: space-between;
         background-color: rgb(245, 245, 245);
            border: 1px solid rgb(220, 220, 220);
            border-radius: 10px;
            padding: 7.5px 15px;
         
         &:first-of-type {
            margin-bottom: 10px;
         }
         > div {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 15px;
            span:first-child {
               width: 8px;
               height: 8px;
               border-radius: 50%;
               &.green { background: #22c55e; }
               &.red { background: #ef4444; }
            }
         }
         ul {
            display: flex;
            gap: 4px;
            li {
               font-size: 11px;
               padding: 2px 6px;
               border-radius: 4px;
            }
         }
      }
   }
`;